import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { CategoryBox } from 'Screens/LearningPage/components';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

type DepartmentsType = {
  departments: any;
  getClientDepartments: (params) => (dispatch: any) => any;
  selectedType: string;
};

const Departments = ({ getClientDepartments, selectedType }: DepartmentsType) => {
  const [items, setItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setItems([]);
    setCurrentPage(1);
    setHasMore(true);
    setIsInitialLoading(true);
  }, [selectedType]);

  useEffect(() => {
    const fetchPage = async () => {
      if (isFetching || !hasMore) {
        return;
      }

      setIsFetching(true);

      const response: any = await getClientDepartments({
        page: currentPage,
        isCustom: selectedType === 'departments-special',
      });

      if (!response?.departments) {
        setIsInitialLoading(false);
        setIsFetching(false);
        setHasMore(false);
        return;
      }

      const departmentsPayload = response?.departments;
      const nextItems = departmentsPayload?.data ?? [];
      const lastPage = departmentsPayload?.meta?.last_page ?? departmentsPayload?.meta?.lastPage;

      setItems((prev) => (currentPage === 1 ? nextItems : [...prev, ...nextItems]));
      setHasMore(lastPage ? currentPage < lastPage : nextItems.length > 0);
      setIsInitialLoading(false);
      setIsFetching(false);
    };

    fetchPage();
  }, [currentPage, selectedType, getClientDepartments]);

  useEffect(() => {
    const target = loaderRef.current;
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetching && !isInitialLoading) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetching, isInitialLoading]);

  return (
    <div className="departments">
      {items.length > 0 ? (
        items.map((item) => {
          return <CategoryBox data={item} key={item.uuid} />;
        })
      ) : isInitialLoading ? (
        <>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
          <div className="category-box category-box--skeleton">
            <Skeleton height="154px" />
          </div>
        </>
      ) : null}
      {items.length > 0 && isFetching && (
        <div className="category-box category-box--skeleton">
          <Skeleton height="154px" />
        </div>
      )}
      <div ref={loaderRef} />
    </div>
  );
};

export default Departments;
